import { useEffect, useMemo, useState } from 'react';

import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  Box,
  Divider,
  IconButton,
  Tab,
  Tabs,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import kebabcase from 'lodash/kebabCase';
import { useLocation, useParams } from 'react-router-dom';

import { YearbookParams } from '../..';
import ColumnChart from '../../components/Charts/ColumnChart';
import LineColumnChart from '../../components/Charts/LineColumnChart';
import PieChart from '../../components/Charts/PieChart';
import { GradientCircle } from '../../components/GradientCircle';
import { ShareButton } from '../../components/ShareButton';
import { colors, sectionTitleGradient } from '../../constants';

interface ColumnSeries {
  name: string;
  data: number[];
}

interface LineColumnSeries {
  name: string;
  type: 'column' | 'line';
  data: (number | null)[];
}

interface ColumnChartConfig {
  type: 'column';
  series: ColumnSeries[];
  categories: string[];
  stacked?: boolean;
  monetary?: boolean;
}

interface LineColumnChartConfig {
  type: 'line-column';
  series: LineColumnSeries[];
  categories: string[];
}

interface PieChartConfig {
  type: 'pie';
  series: number[];
  categories: string[];
}

export type ChartConfig =
  | ColumnChartConfig
  | LineColumnChartConfig
  | PieChartConfig;

interface ChartMeta {
  title: string;
  link?: string;
  description: string;
}

interface SectionMarketProps {
  id: string;
  title: string;
  intro: string[];
  charts: ChartMeta[];
  getChartConfig: (index: number) => ChartConfig;
  gradientPosition?: 'left' | 'right';
}

export function SectionMarket({
  id,
  title,
  intro,
  charts,
  getChartConfig,
  gradientPosition = 'left',
}: SectionMarketProps) {
  const location = useLocation();
  const { market } = useParams<YearbookParams>();
  const [selectedTab, setSelectedTab] = useState(0);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md'),
  );

  const hasCharts = charts.length > 0;
  const hasActiveChart = hasCharts && selectedTab < charts.length;
  const activeMeta = hasActiveChart ? charts[selectedTab] : undefined;
  const activeConfig = hasActiveChart ? getChartConfig(selectedTab) : null;
  const chartTitlesKey = useMemo(
    () => charts.map((chart) => chart.title).join('|'),
    [charts],
  );
  const chartSlug = useMemo(() => {
    return new URLSearchParams(location.search).get('chart');
  }, [location.search]);

  useEffect(() => {
    setSelectedTab(0);
  }, [market, title, chartTitlesKey]);

  useEffect(() => {
    if (!chartSlug) {
      return;
    }

    const chartIndex = charts.findIndex(
      (chart) => kebabcase(chart.title) === chartSlug,
    );

    if (chartIndex >= 0) {
      setSelectedTab(chartIndex);
    }
  }, [chartSlug, charts]);

  useEffect(() => {
    if (!hasCharts) {
      return;
    }

    if (selectedTab >= charts.length) {
      setSelectedTab(charts.length - 1);
    }
  }, [charts.length, hasCharts, selectedTab]);

  function handleTabChange(_: React.SyntheticEvent, value: number) {
    setSelectedTab(value);
  }

  function handleNav(direction: 'prev' | 'next') {
    setSelectedTab((prev) =>
      direction === 'next'
        ? Math.min(prev + 1, charts.length - 1)
        : Math.max(prev - 1, 0),
    );
  }

  return (
    <Box
      component="section"
      id={id}
      display="flex"
      flexDirection="column"
      gap={4}
      mt={10}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          justifyContent: 'center',
        }}>
        <Typography
          component="h2"
          fontSize={{ xs: 28, md: 40 }}
          fontWeight={700}
          lineHeight={1.2}
          sx={{
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundImage: sectionTitleGradient,
          }}>
          {title}
        </Typography>
        <ShareButton market={market} section={kebabcase(title)} size={24} />
        <GradientCircle
          left={gradientPosition === 'left' ? '0' : undefined}
          right={gradientPosition === 'right' ? '0' : undefined}
          size={isMobile ? 300 : 500}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: { xs: '100%', md: '80%' },
          mx: 'auto',
        }}>
        {intro.map((text, idx) => (
          <Typography
            key={idx}
            variant="body1"
            color={colors.text}
            lineHeight={1.75}>
            {text}
          </Typography>
        ))}
      </Box>

      <Box
        sx={{
          bgcolor: colors.bgSection,
          borderRadius: 3,
          p: { xs: 2, md: 3 },
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}>
        {hasCharts && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons={false}
              sx={{
                flex: 1,
                minHeight: 'auto',
                '& .MuiTabs-flexContainer': { gap: 1, flexWrap: 'nowrap' },
                '& .MuiTabs-indicator': { display: 'none' },
                '& .MuiTabs-scroller': {
                  overflowX: 'auto !important',
                  overflowY: 'hidden !important',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                },
              }}>
              {charts.map((c, idx) => (
                <Tooltip key={c.title} title={c.title} placement="top" arrow>
                  <Tab
                    label={c.title}
                    sx={{
                      minHeight: 36,
                      py: 0.75,
                      px: 2,
                      borderRadius: 6,
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      transition: 'all 0.2s ease-in-out',
                      color: idx === selectedTab ? 'white' : colors.text,
                      bgcolor:
                        idx === selectedTab
                          ? 'primary.main'
                          : 'rgba(255,255,255,0.06)',
                      '&.Mui-selected': {
                        color: 'white',
                        bgcolor: 'primary.main',
                      },
                      '&:hover': {
                        bgcolor:
                          idx === selectedTab
                            ? 'primary.dark'
                            : 'rgba(255,255,255,0.12)',
                      },
                    }}
                  />
                </Tooltip>
              ))}
            </Tabs>

            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
              <IconButton
                size="small"
                aria-label="gráfico anterior"
                disabled={selectedTab === 0}
                onClick={() => handleNav('prev')}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor:
                    selectedTab === 0
                      ? 'rgba(255,255,255,0.06)'
                      : 'primary.main',
                  color: selectedTab === 0 ? 'rgba(255,255,255,0.3)' : 'white',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor:
                      selectedTab === 0
                        ? 'rgba(255,255,255,0.06)'
                        : 'primary.dark',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'rgba(255,255,255,0.06)',
                    color: 'rgba(255,255,255,0.25)',
                  },
                }}>
                <ChevronLeft fontSize="small" />
              </IconButton>

              <IconButton
                size="small"
                aria-label="próximo gráfico"
                disabled={selectedTab === charts.length - 1}
                onClick={() => handleNav('next')}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor:
                    selectedTab === charts.length - 1
                      ? 'rgba(255,255,255,0.06)'
                      : 'primary.main',
                  color:
                    selectedTab === charts.length - 1
                      ? 'rgba(255,255,255,0.3)'
                      : 'white',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor:
                      selectedTab === charts.length - 1
                        ? 'rgba(255,255,255,0.06)'
                        : 'primary.dark',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'rgba(255,255,255,0.06)',
                    color: 'rgba(255,255,255,0.25)',
                  },
                }}>
                <ChevronRight fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )}

        {hasCharts && activeMeta && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              width: { xs: '100%', md: '80%' },
              mx: 'auto',
            }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mt: 2,
              }}>
              <Typography
                variant="h6"
                color="white"
                fontWeight={700}
                textTransform="uppercase"
                lineHeight={1.3}>
                &mdash; {activeMeta.title}
              </Typography>
              <ShareButton
                market={market}
                section={`${kebabcase(title)}?chart=${kebabcase(activeMeta.title)}`}
                size={20}
              />
            </Box>
            <Typography variant="body2" color={colors.text} lineHeight={1.75}>
              {activeMeta.description}
            </Typography>
          </Box>
        )}

        {activeConfig && (
          <Box sx={{ width: '100%' }}>
            <Divider
              sx={{
                mt: 2,
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                mx: 8,
                position: 'relative',
                height: 2,
              }}>
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '50%',
                  background: 'linear-gradient(to right, #1A1A1A, transparent)',
                  zIndex: 2,
                  pointerEvents: 'none',
                }}
              />

              <Box
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: '50%',
                  background: 'linear-gradient(to left, #1A1A1A, transparent)',
                  zIndex: 2,
                  pointerEvents: 'none',
                }}
              />
            </Divider>
            {activeConfig.type === 'column' && (
              <ColumnChart
                series={activeConfig.series}
                categories={activeConfig.categories}
                stacked={activeConfig.stacked}
                monetary={activeConfig.monetary}
              />
            )}

            {activeConfig.type === 'line-column' && (
              <LineColumnChart
                series={activeConfig.series}
                categories={activeConfig.categories}
              />
            )}

            {activeConfig.type === 'pie' && (
              <PieChart
                series={activeConfig.series}
                categories={activeConfig.categories}
              />
            )}
          </Box>
        )}

        {!hasCharts && (
          <Box
            sx={{
              borderRadius: 2,
              border: '1px dashed rgba(255,255,255,0.16)',
              p: 3,
            }}>
            <Typography color={colors.text}>
              Gráficos desta seção ainda não foram disponibilizados para este
              mercado.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
